import React, { useEffect, useState, useRef } from "react"
import { connect } from "react-redux"
import { Row, Col, Card, CardBody, Table, Button, Modal, ModalHeader, ModalBody, ModalFooter, Input } from "reactstrap"
import { setBreadcrumbItems } from "../../../store/actions"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"
import { useNavigate } from "react-router-dom"
import PaymentApi from "../../../Api/paymentApi"
import { OverlayPanel } from "primereact/overlaypanel"
import { StandardPagination } from "components/Common"
import "./Transactions.scss"

const Transactions = props => {
    document.title = "Transactions | Pixwik - Admin"
    const navigate = useNavigate()
    const accessToken = localStorage.getItem("accessToken")
    const statusFilterRef = useRef(null)

    const [loading, setLoading] = useState(false)
    const [tableLoading, setTableLoading] = useState(false)
    const [metaData, setMetaData] = useState({
        todayTotalTransactions: 0,
        thisWeekTotalTransactions: 0,
        thisMonthTotalTransactions: 0,
        thisYearTotalTransactions: 0,
        totalTransactionsCount: 0,
        totalTransactionAmount: 0
    })
    const [paymentHistory, setPaymentHistory] = useState([])
    const [filteredPaymentHistory, setFilteredPaymentHistory] = useState([])
    const [searchTerm, setSearchTerm] = useState("")
    const [statusFilter, setStatusFilter] = useState("all")
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(0)
    const [totalRecords, setTotalRecords] = useState(0)
    const itemsPerPage = 10
    const [error, setError] = useState(null)
    const [modalOpen, setModalOpen] = useState(false)
    const [selectedTransaction, setSelectedTransaction] = useState(null)

    const breadcrumbItems = [
        { label: "Pixwik", link: "/" },
        { label: "Payments", link: "#" },
        { label: "Transactions", link: "#" },
    ]

    const handleBreadcrumbClick = link => {
        if (link !== "#") {
            navigate(link)
        }
    }

    const fetchPaymentMetaData = async () => {
        try {
            setLoading(true)
            setError(null)
            const config = {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            }
            const response = await PaymentApi.getMetaData(config)
            if (response.data.status && response.data.data) {
                setMetaData(response.data.data)
            }
        } catch (error) {
            setError("Failed to load payment statistics")
        } finally {
            setLoading(false)
        }
    }

    const fetchPaymentHistory = async (page = currentPage) => {
        try {
            setTableLoading(true)
            setError(null)
            const config = {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                params: {
                    page,
                    limit: itemsPerPage
                }
            }
            const response = await PaymentApi.getPaymentHistory(config)
            if (response.data.status && response.data.data) {
                // Updated to match new API response structure
                const payments = Array.isArray(response.data.data) ? response.data.data : []
                setPaymentHistory(payments)
                setFilteredPaymentHistory(payments)

                // Updated pagination mapping
                const pagination = response.data.pagination || {}
                setTotalPages(pagination.pages || 0)
                setTotalRecords(pagination.total || 0)
                setCurrentPage(pagination.page || 1)
            }
        } catch (error) {
            setError("Failed to load payment history")
        } finally {
            setTableLoading(false)
        }
    }

    useEffect(() => {
        props.setBreadcrumbItems("Transactions", breadcrumbItems)
        fetchPaymentMetaData()
        fetchPaymentHistory()
    }, [])

    // Filter transactions based on search term and status
    useEffect(() => {
        let filtered = paymentHistory

        // Filter by status
        if (statusFilter !== "all") {
            filtered = filtered.filter(payment => payment.paymentStatus?.toLowerCase() === statusFilter.toLowerCase())
        }

        // Filter by search term
        if (searchTerm) {
            filtered = filtered.filter(payment =>
                payment.userId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                payment.orderId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                payment.cashFreeOrderId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                payment.amount?.toString().includes(searchTerm) ||
                payment.paymentStatus?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                payment.orderFor?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                payment.orderForDetails?.circleName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                payment.orderForDetails?.title?.toLowerCase().includes(searchTerm.toLowerCase())
            )
        }

        setFilteredPaymentHistory(filtered)
    }, [searchTerm, paymentHistory, statusFilter])

    // Transaction stats from API
    const transactionStats = [
        {
            title: "Today",
            iconClass: "ion-ios-calendar",
            color: "primary",
            total: `₹${Number(metaData.todayTotalTransactions || 0).toLocaleString()}`,
            subtitle: "Transactions Today"
        },
        {
            title: "This Week",
            iconClass: "ion-md-calendar",
            color: "success",
            total: `₹${Number(metaData.thisWeekTotalTransactions || 0).toLocaleString()}`,
            subtitle: "Weekly Transactions"
        },
        {
            title: "This Month",
            iconClass: "ion-ios-stats",
            color: "warning",
            total: `₹${Number(metaData.thisMonthTotalTransactions || 0).toLocaleString()}`,
            subtitle: "Monthly Transactions"
        },
        {
            title: "This Year",
            iconClass: "ion-md-stats",
            color: "danger",
            total: `₹${Number(metaData.thisYearTotalTransactions || 0).toLocaleString()}`,
            subtitle: "Yearly Transactions"
        },
        {
            title: "Total Amount",
            iconClass: "ion-md-cash",
            color: "info",
            total: `₹${Number(metaData.totalTransactionAmount || 0).toLocaleString()}`,
            subtitle: "Total Transaction Amount"
        },
    ]

    // Helper functions
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })
    }

    const getPaymentMethodDisplay = (paymentMode) => {
        if (!paymentMode) return 'N/A'
        try {
            const parsed = JSON.parse(paymentMode)
            if (parsed.upi) return 'UPI'
            if (parsed.app) return parsed.app.provider || 'Wallet'
            return 'Other'
        } catch {
            return 'N/A'
        }
    }

    const getStatusBadge = (status) => {
        switch (status?.toUpperCase()) {
            case 'SUCCESS':
                return 'success'
            case 'PENDING':
                return 'warning'
            case 'ACTIVE':
                return 'info'
            case 'FAILED':
                return 'danger'
            default:
                return 'secondary'
        }
    }

    const handleViewTransaction = (payment) => {
        setSelectedTransaction(payment)
        setModalOpen(true)
    }

    const toggleModal = () => {
        setModalOpen(!modalOpen)
        if (modalOpen) {
            setSelectedTransaction(null)
        }
    }

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage)
            fetchPaymentHistory(newPage)
        }
    }

    return (
        <React.Fragment>
            <Row>
                <Col xs="12">
                    <div className="transaction-header mb-4">
                        <CustomBreadCrumb
                            data={breadcrumbItems}
                            pageTitle="Transactions"
                            handleClick={handleBreadcrumbClick}
                        />
                    </div>

                    {/* Error Display */}
                    {error && (
                        <Row className="mb-4">
                            <Col xs="12">
                                <div className="alert alert-danger d-flex align-items-center" role="alert">
                                    <i className="mdi mdi-alert-circle me-2"></i>
                                    {error}
                                    <Button
                                        color="link"
                                        size="sm"
                                        className="ms-auto text-danger"
                                        onClick={() => {
                                            fetchPaymentMetaData()
                                            fetchPaymentHistory(currentPage)
                                        }}
                                    >
                                        Retry
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* Transaction Stats Cards - All 5 Cards in Same Row */}
                    <div className="transaction-stats-container mb-4">
                        <div className="transaction-stats-grid">
                            {transactionStats.map((stat, key) => (
                                <div key={key} className="transaction-stat-item">
                                    <Card className={`mini-stat bg-${stat.color} transaction-card h-100`}>
                                        <CardBody className="card-body mini-stat-img">
                                            <div className="d-flex align-items-center h-100">
                                                <div className="mini-stat-icon me-3">
                                                    <i className={`ion ${stat.iconClass}`}></i>
                                                </div>
                                                <div className="text-white flex-grow-1">
                                                    <h6 className="text-uppercase mb-2 text-white" style={{ fontSize: '0.75rem', fontWeight: '600' }}>
                                                        {stat.title}
                                                    </h6>
                                                    <h4 className="text-white mb-0" style={{ fontSize: '1.4rem', fontWeight: '700' }}>
                                                        {loading ? (
                                                            <div className="spinner-border spinner-border-sm text-white" role="status">
                                                                <span className="sr-only">Loading...</span>
                                                            </div>
                                                        ) : (
                                                            typeof stat.total === 'string' ? stat.total : stat.total.toLocaleString()
                                                        )}
                                                    </h4>
                                                </div>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Transactions Table */}
                    <Card>
                        <CardBody>
                            <div className="d-flex align-items-center justify-content-between mb-4">
                                <div>
                                    <h4 className="card-title mb-1">Payment History</h4>
                                    {totalRecords > 0 && (
                                        <small className="text-muted">
                                            Total {totalRecords} transactions
                                        </small>
                                    )}
                                </div>
                                <div className="d-flex gap-2">
                                    <Input
                                        type="text"
                                        placeholder="Search transactions..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        style={{ width: '250px' }}
                                        className="form-control-sm"
                                    />
                                    <Button
                                        color="outline-primary"
                                        size="sm"
                                        onClick={() => {
                                            fetchPaymentMetaData()
                                            fetchPaymentHistory(currentPage)
                                        }}
                                        disabled={loading || tableLoading}
                                    >
                                        <i className="mdi mdi-refresh me-1"></i>
                                        Refresh
                                    </Button>
                                </div>
                            </div>

                            <div className="data-table-container">
                                <div className="table-responsive">
                                    {tableLoading ? (
                                        <div className="text-center p-4">
                                            <div className="spinner-border" role="status">
                                                <span className="sr-only">Loading...</span>
                                            </div>
                                            <p className="mt-2">Loading transactions...</p>
                                        </div>
                                    ) : (
                                        <Table className="table mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>S.No</th>
                                                    <th>User ID</th>
                                                    <th>Order ID</th>
                                                    <th>Amount</th>
                                                    <th>Payment Method</th>
                                                    <th>
                                                        <span className="d-flex align-items-center gap-2 justify-content-center">
                                                            <span>Status</span>
                                                            <span
                                                                onClick={e => statusFilterRef.current.toggle(e)}
                                                                style={{ cursor: "pointer" }}
                                                            >
                                                                <i className="mdi mdi-filter" />
                                                            </span>
                                                        </span>
                                                        <OverlayPanel ref={statusFilterRef}>
                                                            <Input
                                                                type="select"
                                                                value={statusFilter}
                                                                onChange={e => {
                                                                    setStatusFilter(e.target.value)
                                                                    statusFilterRef.current.hide()
                                                                }}
                                                                style={{ width: "150px" }}
                                                            >
                                                                <option value="all">All Status</option>
                                                                <option value="success">Success</option>
                                                                <option value="pending">Pending</option>
                                                                <option value="failed">Failed</option>
                                                                <option value="active">Active</option>
                                                            </Input>
                                                        </OverlayPanel>
                                                    </th>
                                                    <th>Order For</th>
                                                    <th>Date</th>
                                                    <th>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredPaymentHistory.length > 0 ? (
                                                    filteredPaymentHistory.map((payment, index) => (
                                                        <tr key={payment._id} className="transaction-row">
                                                            <th scope="row">{((currentPage - 1) * itemsPerPage) + index + 1}</th>
                                                            <td>
                                                                <span className="text-truncate d-inline-block" style={{ maxWidth: '100px' }}>
                                                                    {payment.userId || 'N/A'}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span className="transaction-id text-truncate d-inline-block" style={{ maxWidth: '150px' }}>
                                                                    {payment.orderId}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span className="amount fw-bold">
                                                                    ₹{payment.amount} {payment.currency}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span className="badge bg-secondary">
                                                                    {getPaymentMethodDisplay(payment.paymentMode)}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span className={`badge bg-${getStatusBadge(payment.paymentStatus)}`}>
                                                                    {payment.paymentStatus}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <div>
                                                                    <span className="badge bg-info mb-1 d-block">
                                                                        {payment.orderFor}
                                                                    </span>
                                                                    <small className="text-muted text-truncate d-block" style={{ maxWidth: '120px' }}>
                                                                        {payment.orderForDetails?.circleName || payment.orderForDetails?.title || 'N/A'}
                                                                    </small>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div>
                                                                    <small className="d-block">
                                                                        <strong>Created:</strong> {formatDate(payment.createdAt)}
                                                                    </small>
                                                                    {payment.paymentTime && (
                                                                        <small className="text-success d-block">
                                                                            <strong>Paid:</strong> {formatDate(payment.paymentTime)}
                                                                        </small>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <Button
                                                                    color="info"
                                                                    size="sm"
                                                                    onClick={() => handleViewTransaction(payment)}
                                                                    className="view-btn"
                                                                >
                                                                    <i className="mdi mdi-eye me-1"></i>
                                                                    View
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="9" className="text-center p-4">
                                                            <div className="text-muted">
                                                                <i className="mdi mdi-information-outline me-2"></i>
                                                                No transactions found
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    )}
                                </div>
                            </div>
                        </CardBody>
                    </Card>

                    {/* Pagination */}
                    <StandardPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                </Col>
            </Row>

            {/* Transaction Details Modal */}
            <Modal isOpen={modalOpen} toggle={toggleModal} size="xl" centered>
                <ModalHeader toggle={toggleModal}>
                    Transaction Details
                </ModalHeader>
                <ModalBody style={{ maxHeight: '80vh', overflowY: 'auto', padding: '20px' }}>
                    {selectedTransaction && (
                        <div className="transaction-details">
                            {/* Basic Info */}
                            <div className="mb-4">
                                <h5 className="mb-3">Basic Information</h5>
                                <Row>
                                    <Col md={6} className="mb-3">
                                        <label className="text-muted small">Transaction ID</label>
                                        <p className="mb-0 fw-medium">{selectedTransaction._id}</p>
                                    </Col>
                                    <Col md={6} className="mb-3">
                                        <label className="text-muted small">Order ID</label>
                                        <p className="mb-0 fw-medium">{selectedTransaction.orderId}</p>
                                    </Col>
                                    <Col md={6} className="mb-3">
                                        <label className="text-muted small">Cashfree Order ID</label>
                                        <p className="mb-0 fw-medium">{selectedTransaction.cashFreeOrderId}</p>
                                    </Col>
                                    <Col md={6} className="mb-3">
                                        <label className="text-muted small">User ID</label>
                                        <p className="mb-0 fw-medium">{selectedTransaction.userId}</p>
                                    </Col>
                                </Row>
                            </div>

                            {/* Payment Info */}
                            <div className="mb-4">
                                <h5 className="mb-3">Payment Information</h5>
                                <Row>
                                    <Col md={6} className="mb-3">
                                        <label className="text-muted small">Amount</label>
                                        <p className="mb-0 fw-bold amount">
                                            ₹{selectedTransaction.amount} {selectedTransaction.currency}
                                        </p>
                                    </Col>
                                    <Col md={6} className="mb-3">
                                        <label className="text-muted small">Payment Status</label>
                                        <p className="mb-0">
                                            <span className={`badge bg-${getStatusBadge(selectedTransaction.paymentStatus)}`}>
                                                {selectedTransaction.paymentStatus}
                                            </span>
                                        </p>
                                    </Col>
                                    <Col md={6} className="mb-3">
                                        <label className="text-muted small">Payment Method</label>
                                        <p className="mb-0">
                                            <span className="badge bg-secondary">
                                                {getPaymentMethodDisplay(selectedTransaction.paymentMode)}
                                            </span>
                                        </p>
                                    </Col>
                                    <Col md={6} className="mb-3">
                                        <label className="text-muted small">Payment Group</label>
                                        <p className="mb-0 fw-medium">{selectedTransaction.paymentGroup || 'N/A'}</p>
                                    </Col>
                                    {selectedTransaction.bankReferenceId && (
                                        <Col md={6} className="mb-3">
                                            <label className="text-muted small">Bank Reference ID</label>
                                            <p className="mb-0 fw-medium">{selectedTransaction.bankReferenceId}</p>
                                        </Col>
                                    )}
                                    {selectedTransaction.paymentMessage && (
                                        <Col md={6} className="mb-3">
                                            <label className="text-muted small">Payment Message</label>
                                            <p className="mb-0 fw-medium">{selectedTransaction.paymentMessage}</p>
                                        </Col>
                                    )}
                                    {selectedTransaction.paidTo && (
                                        <Col md={6} className="mb-3">
                                            <label className="text-muted small">Paid To</label>
                                            <p className="mb-0 fw-medium">{selectedTransaction.paidTo}</p>
                                        </Col>
                                    )}
                                </Row>
                            </div>

                            {/* Order Details */}
                            {selectedTransaction.orderForDetails && (
                                <div className="mb-4">
                                    <h5 className="mb-3">Order Details</h5>
                                    <Row>
                                        <Col md={6} className="mb-3">
                                            <label className="text-muted small">Order For</label>
                                            <p className="mb-0 fw-medium">{selectedTransaction.orderFor}</p>
                                        </Col>
                                        <Col md={6} className="mb-3">
                                            <label className="text-muted small">Order Status</label>
                                            <p className="mb-0">
                                                <span className={`badge bg-${getStatusBadge(selectedTransaction.orderStatus)}`}>
                                                    {selectedTransaction.orderStatus}
                                                </span>
                                            </p>
                                        </Col>
                                        {/* Circle Details */}
                                        {selectedTransaction.orderForDetails.circleName && (
                                            <Col md={12} className="mb-3">
                                                <label className="text-muted small">Circle Name</label>
                                                <p className="mb-0 fw-medium">{selectedTransaction.orderForDetails.circleName}</p>
                                            </Col>
                                        )}

                                        {/* Course Details */}
                                        {selectedTransaction.orderForDetails.title && (
                                            <Col md={12} className="mb-3">
                                                <label className="text-muted small">Course Title</label>
                                                <p className="mb-0 fw-medium">{selectedTransaction.orderForDetails.title}</p>
                                            </Col>
                                        )}

                                        {selectedTransaction.orderForDetails.description && (
                                            <Col md={12} className="mb-3">
                                                <label className="text-muted small">Description</label>
                                                <p className="mb-0">{selectedTransaction.orderForDetails.description}</p>
                                            </Col>
                                        )}

                                        {/* Categories for Circle */}
                                        {selectedTransaction.orderForDetails.circleCategory && (
                                            <Col md={12} className="mb-3">
                                                <label className="text-muted small">Circle Categories</label>
                                                <div className="d-flex gap-2 flex-wrap">
                                                    {selectedTransaction.orderForDetails.circleCategory.map((cat, idx) => (
                                                        <span key={idx} className="badge bg-info">{cat}</span>
                                                    ))}
                                                </div>
                                            </Col>
                                        )}

                                        {/* Tags for Course */}
                                        {selectedTransaction.orderForDetails.tags && (
                                            <Col md={12} className="mb-3">
                                                <label className="text-muted small">Course Tags</label>
                                                <div className="d-flex gap-2 flex-wrap">
                                                    {selectedTransaction.orderForDetails.tags.map((tag, idx) => (
                                                        <span key={idx} className="badge bg-secondary">{tag}</span>
                                                    ))}
                                                </div>
                                            </Col>
                                        )}

                                        {/* Course specific fields */}
                                        {selectedTransaction.orderForDetails.difficulty && (
                                            <Col md={6} className="mb-3">
                                                <label className="text-muted small">Difficulty</label>
                                                <p className="mb-0">
                                                    <span className="badge bg-warning text-capitalize">
                                                        {selectedTransaction.orderForDetails.difficulty}
                                                    </span>
                                                </p>
                                            </Col>
                                        )}

                                        {selectedTransaction.orderForDetails.price && (
                                            <Col md={6} className="mb-3">
                                                <label className="text-muted small">Course Price</label>
                                                <p className="mb-0 fw-bold">
                                                    ₹{selectedTransaction.orderForDetails.price} {selectedTransaction.orderForDetails.currency}
                                                </p>
                                            </Col>
                                        )}
                                    </Row>
                                </div>
                            )}

                            {/* Customer Details */}
                            {selectedTransaction.orderDetails?.customer_details && (
                                <div className="mb-4">
                                    <h5 className="mb-3">Customer Details</h5>
                                    <Row>
                                        <Col md={6} className="mb-3">
                                            <label className="text-muted small">Customer Name</label>
                                            <p className="mb-0 fw-medium">
                                                {selectedTransaction.orderDetails.customer_details.customer_name || 'N/A'}
                                            </p>
                                        </Col>
                                        <Col md={6} className="mb-3">
                                            <label className="text-muted small">Customer Email</label>
                                            <p className="mb-0 fw-medium">
                                                {selectedTransaction.orderDetails.customer_details.customer_email || 'N/A'}
                                            </p>
                                        </Col>
                                        <Col md={6} className="mb-3">
                                            <label className="text-muted small">Customer Phone</label>
                                            <p className="mb-0 fw-medium">
                                                {selectedTransaction.orderDetails.customer_details.customer_phone || 'N/A'}
                                            </p>
                                        </Col>
                                    </Row>
                                </div>
                            )}

                            {/* Timestamps */}
                            <div className="mb-2">
                                <h5 className="mb-3">Timestamps</h5>
                                <Row>
                                    <Col md={6} className="mb-3">
                                        <label className="text-muted small">Created At</label>
                                        <p className="mb-0 fw-medium">{formatDate(selectedTransaction.createdAt)}</p>
                                    </Col>
                                    <Col md={6} className="mb-3">
                                        <label className="text-muted small">Updated At</label>
                                        <p className="mb-0 fw-medium">{formatDate(selectedTransaction.updatedAt)}</p>
                                    </Col>
                                    {selectedTransaction.paymentTime && (
                                        <Col md={6} className="mb-3">
                                            <label className="text-muted small">Payment Time</label>
                                            <p className="mb-0 fw-medium">{formatDate(selectedTransaction.paymentTime)}</p>
                                        </Col>
                                    )}
                                </Row>
                            </div>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleModal}>
                        Close
                    </Button>
                </ModalFooter>
            </Modal>
        </React.Fragment>
    )
}

export default connect(null, { setBreadcrumbItems })(Transactions)